/**
 * Homepage (and elsewhere) section lazy-load via Shopify Section Rendering API.
 * Placeholders: [data-lazy-section][data-section-id]
 * Full HTML is requested with ?section_lazy_render=1 so Liquid skips the placeholder.
 *
 * Manual mode (content-tabs panels index > 0):
 * - data-lazy-manual="true" or auto-detected as a deferred tab panel
 * - skips IntersectionObserver; load via BoyaLazySection.load / loadIn
 */
(function () {
  if (window.__boyaLazySectionInit) return;
  window.__boyaLazySectionInit = true;

  var loading = Object.create(null);

  function getRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return "/";
  }

  function buildUrl(sectionId, attempt, currentPage) {
    var root = getRoot();
    var url = new URL(root, window.location.origin);
    if (currentPage) url.pathname = window.location.pathname;
    url.searchParams.set("sections", sectionId);
    url.searchParams.set("section_lazy_render", "1");
    if (attempt > 0) {
      url.searchParams.set("section_lazy_attempt", String(attempt));
    }
    return url.pathname + url.search;
  }

  function toInteger(value, fallback) {
    var number = parseInt(value, 10);
    return Number.isFinite(number) ? number : fallback;
  }

  function clamp(number, min, max) {
    return Math.min(max, Math.max(min, number));
  }

  function getRetryConfig(placeholder) {
    var maxRetries = clamp(
      toInteger(placeholder.getAttribute("data-lazy-max-retries"), 0),
      0,
      5
    );
    var retryDelays = (placeholder.getAttribute("data-lazy-retry-delays") || "1200,3500")
      .split(",")
      .map(function (value) {
        return toInteger(value, 0);
      })
      .filter(function (value) {
        return value >= 0;
      });

    if (!retryDelays.length) retryDelays = [1200, 3500];

    return {
      maxRetries: maxRetries,
      retryDelays: retryDelays,
      retryJitter: clamp(
        toInteger(placeholder.getAttribute("data-lazy-retry-jitter"), 0),
        0,
        2000
      ),
      fetchTimeout: clamp(
        toInteger(placeholder.getAttribute("data-lazy-fetch-timeout"), 0),
        0,
        30000
      ),
      currentPage: placeholder.getAttribute("data-lazy-current-page") === "true",
    };
  }

  function getRetryDelay(config, attempt) {
    var index = Math.min(attempt, config.retryDelays.length - 1);
    var baseDelay = config.retryDelays[index];
    var jitter = config.retryJitter
      ? Math.floor(Math.random() * (config.retryJitter + 1))
      : 0;
    return baseDelay + jitter;
  }

  function wait(delay) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, delay);
    });
  }

  function createLazyError(message, status, retryable) {
    var error = new Error(message);
    error.lazyStatus = status || 0;
    error.lazyRetryable = Boolean(retryable);
    return error;
  }

  function fetchSectionData(sectionId, attempt, config) {
    var controller = "AbortController" in window ? new AbortController() : null;
    var timeoutId = controller && config.fetchTimeout > 0
      ? window.setTimeout(function () {
          controller.abort();
        }, config.fetchTimeout)
      : null;
    // Do not force `Accept: application/json` here. On Shopify product URLs that
    // header selects the Product Ajax JSON response instead of the Section
    // Rendering API response, even when the `sections` query is present.
    // The browser's default Accept header lets Shopify return the expected
    // `{ sectionId: "<section html>" }` payload on every page type/locale.
    var options = { credentials: "same-origin" };

    if (controller) options.signal = controller.signal;

    return Promise.resolve().then(function () {
      return fetch(buildUrl(sectionId, attempt, config.currentPage), options);
    })
      .then(function (response) {
        if (!response.ok) {
          var status = response.status;
          var responseError = createLazyError(
            "lazy-section " + status,
            status,
            status === 408 || status === 429 || status >= 500
          );
          var retryAfter = response.headers && response.headers.get("Retry-After");
          if (retryAfter) {
            var seconds = Number(retryAfter);
            var retryAfterMs = Number.isFinite(seconds)
              ? seconds * 1000
              : Date.parse(retryAfter) - Date.now();
            if (Number.isFinite(retryAfterMs)) {
              responseError.lazyRetryAfter = Math.max(0, retryAfterMs);
            }
          }
          throw responseError;
        }
        return response.json().catch(function () {
          throw createLazyError("lazy-section invalid JSON", response.status, true);
        });
      })
      .then(function (data) {
        if (timeoutId) window.clearTimeout(timeoutId);
        return data;
      })
      .catch(function (error) {
        if (timeoutId) window.clearTimeout(timeoutId);
        if (error && error.name === "AbortError") {
          throw createLazyError("lazy-section request timed out", 0, true);
        }
        if (!error || typeof error !== "object") {
          error = createLazyError("lazy-section network error", 0, true);
        }
        if (typeof error.lazyRetryable !== "boolean") {
          error.lazyRetryable = true;
        }
        throw error;
      });
  }

  function clearRecovery(placeholder) {
    if (placeholder && typeof placeholder.__boyaLazyRecoveryCleanup === "function") {
      placeholder.__boyaLazyRecoveryCleanup();
    }
  }

  function scheduleRecovery(placeholder) {
    if (
      !placeholder ||
      placeholder.getAttribute("data-lazy-recover") !== "true" ||
      !document.documentElement.contains(placeholder)
    ) {
      return;
    }

    clearRecovery(placeholder);

    var observer = null;
    var hasExited = false;
    var recovered = false;

    function cleanup() {
      window.removeEventListener("online", onOnline);
      if (observer) observer.disconnect();
      observer = null;
      placeholder.removeAttribute("data-lazy-recovery-bound");
      placeholder.__boyaLazyRecoveryCleanup = null;
    }

    function retry(reason) {
      if (recovered) return;
      if (!document.documentElement.contains(placeholder)) {
        cleanup();
        return;
      }
      recovered = true;
      cleanup();
      placeholder.removeAttribute("data-lazy-error");
      placeholder.setAttribute("data-lazy-recovery-reason", reason);
      loadSection(placeholder);
    }

    function onOnline() {
      retry("online");
    }

    placeholder.setAttribute("data-lazy-recovery-bound", "true");
    placeholder.__boyaLazyRecoveryCleanup = cleanup;
    window.addEventListener("online", onOnline);

    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) {
              hasExited = true;
              return;
            }
            if (hasExited) retry("reenter");
          });
        },
        {
          rootMargin: "0px",
          threshold: 0,
        }
      );
      observer.observe(placeholder);
    }
  }

  function runScripts(root) {
    root.querySelectorAll("script").forEach(function (oldScript) {
      var script = document.createElement("script");
      Array.from(oldScript.attributes).forEach(function (attr) {
        script.setAttribute(attr.name, attr.value);
      });
      script.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(script, oldScript);
    });
  }

  function dispatchSectionLoad(sectionEl) {
    try {
      document.dispatchEvent(
        new CustomEvent("shopify:section:load", {
          bubbles: true,
          detail: { sectionId: sectionEl.id && sectionEl.id.replace(/^shopify-section-/, "") },
        })
      );
    } catch (e) {}
    try {
      sectionEl.dispatchEvent(new CustomEvent("boya:lazy-section:loaded", { bubbles: true }));
    } catch (e) {}
  }

  function replaceSection(placeholder, html, sectionId) {
    var host =
      placeholder.closest(".shopify-section") ||
      document.getElementById("shopify-section-" + sectionId);
    if (!host) return null;

    var doc = new DOMParser().parseFromString(html, "text/html");
    var next = doc.body.firstElementChild;
    if (!next) return null;
    if (placeholder.getAttribute("data-lazy-recover") === "true" && (
      next.id !== "shopify-section-" + sectionId ||
      next.querySelector("[data-lazy-section]")
    )) {
      throw createLazyError("lazy-section invalid section HTML for " + sectionId, 0, true);
    }

    host.replaceWith(next);
    runScripts(next);
    dispatchSectionLoad(next);
    return next;
  }

  function getModulesCountFromBtn(btn) {
    var n = parseInt(btn && btn.getAttribute("data-modules-count"), 10);
    return !n || n < 1 ? 1 : n;
  }

  /**
   * Total sibling sections controlled by content-tabs =
   * sum of each tab's data-modules-count (default 1).
   * Panel index > 0 → manual load (avoid IO prefetch while hidden).
   */
  function getContentTabsPanelIndex(sectionEl) {
    if (!sectionEl || !sectionEl.parentNode) return -1;
    var kids = sectionEl.parentNode.children;
    var tabHost = null;
    var totalPanels = 0;

    for (var i = 0; i < kids.length; i++) {
      var el = kids[i];
      if (!el.classList || !el.classList.contains("shopify-section")) continue;
      var tabsRoot = el.querySelector("[data-content-tabs]");
      if (!tabsRoot) continue;
      tabHost = el;
      var btns = tabsRoot.querySelectorAll(".content-tabs__tab, .content-tabs__btn");
      for (var b = 0; b < btns.length; b++) {
        totalPanels += getModulesCountFromBtn(btns[b]);
      }
      break;
    }

    if (!tabHost || totalPanels < 1) return -1;

    var panels = [];
    var started = false;
    for (var j = 0; j < kids.length && panels.length < totalPanels; j++) {
      var s = kids[j];
      if (!started) {
        if (s === tabHost) started = true;
        continue;
      }
      if (s.classList && s.classList.contains("shopify-section")) {
        panels.push(s);
      }
    }

    return panels.indexOf(sectionEl);
  }

  function shouldManualLoad(placeholder) {
    if (!placeholder) return false;
    if (placeholder.getAttribute("data-lazy-manual") === "true") return true;
    var host =
      placeholder.closest(".shopify-section") ||
      document.getElementById("shopify-section-" + placeholder.getAttribute("data-section-id"));
    return getContentTabsPanelIndex(host) > 0;
  }

  function loadSection(placeholder) {
    if (!placeholder) return Promise.resolve(null);
    if (!document.documentElement.contains(placeholder)) return Promise.resolve(null);

    var sectionId = placeholder.getAttribute("data-section-id");
    if (!sectionId) return Promise.resolve(null);

    if (loading[sectionId]) {
      return loading[sectionId];
    }

    clearRecovery(placeholder);
    var config = getRetryConfig(placeholder);

    // The placeholder uses small responsive preview images to avoid blank
    // cards during a fast scroll. Keep them lazy while the page is idle, then
    // promote them only when the section request actually begins.
    placeholder.querySelectorAll("img[loading='lazy']").forEach(function (image) {
      image.loading = "eager";
      image.setAttribute("loading", "eager");
    });

    placeholder.setAttribute("aria-busy", "true");
    placeholder.setAttribute("data-lazy-state", "loading");
    placeholder.removeAttribute("data-lazy-error");
    placeholder.removeAttribute("data-lazy-recovery-reason");

    function runAttempt(attempt) {
      placeholder.setAttribute("data-lazy-attempt", String(attempt + 1));
      return fetchSectionData(sectionId, attempt, config)
        .then(function (data) {
          var html = data && data[sectionId];
          if (typeof html !== "string" || !html.trim()) {
            throw createLazyError(
              "lazy-section invalid Section Rendering API payload for " + sectionId,
              "payload",
              false
            );
          }
          if (!document.documentElement.contains(placeholder)) return null;
          var next = replaceSection(placeholder, html, sectionId);
          if (!next) {
            throw createLazyError(
              "lazy-section replacement failed for " + sectionId,
              0,
              false
            );
          }
          return next;
        })
        .catch(function (error) {
          if (
            error.lazyRetryable &&
            attempt < config.maxRetries &&
            document.documentElement.contains(placeholder)
          ) {
            var delay = getRetryDelay(config, attempt);
            // Respect Retry-After without overflowing the browser's timer limit.
            delay = Math.min(2147483647, Math.max(delay, error.lazyRetryAfter || 0));
            placeholder.setAttribute("data-lazy-state", "retrying");
            placeholder.setAttribute("data-lazy-retry-in", String(delay));
            return wait(delay).then(function () {
              if (!document.documentElement.contains(placeholder)) return null;
              placeholder.setAttribute("data-lazy-state", "loading");
              placeholder.removeAttribute("data-lazy-retry-in");
              return runAttempt(attempt + 1);
            });
          }
          throw error;
        });
    }

    loading[sectionId] = runAttempt(0).then(
      function (next) {
        delete loading[sectionId];
        return next;
      },
      function (error) {
        delete loading[sectionId];
        if (document.documentElement.contains(placeholder)) {
          placeholder.removeAttribute("aria-busy");
          placeholder.removeAttribute("data-lazy-retry-in");
          placeholder.setAttribute("data-lazy-state", "error");
          placeholder.setAttribute("data-lazy-error", String(error.lazyStatus || "network"));
          if (error.lazyRetryable) scheduleRecovery(placeholder);
        }
        console.warn("[lazy-section]", error);
        return null;
      }
    );

    return loading[sectionId];
  }

  function loadIn(root) {
    if (!root) return Promise.resolve([]);
    var list = root.querySelectorAll
      ? root.querySelectorAll("[data-lazy-section][data-section-id]")
      : [];
    if (!list.length) {
      if (root.matches && root.matches("[data-lazy-section][data-section-id]")) {
        return loadSection(root).then(function (el) {
          return el ? [el] : [];
        });
      }
      return Promise.resolve([]);
    }
    return Promise.all(Array.prototype.map.call(list, loadSection));
  }

  function observe(placeholder) {
    if (placeholder.getAttribute("data-lazy-bound") === "true") return;
    placeholder.setAttribute("data-lazy-bound", "true");

    if (window.Shopify && window.Shopify.designMode) {
      loadSection(placeholder);
      return;
    }

    if (shouldManualLoad(placeholder)) {
      placeholder.setAttribute("data-lazy-manual", "true");
      return;
    }

    if (!("IntersectionObserver" in window)) {
      loadSection(placeholder);
      return;
    }

    var requireScroll = placeholder.getAttribute("data-lazy-require-scroll") === "true";

    function startObserver() {
      var desktopRootMargin = placeholder.getAttribute("data-lazy-root-margin") || "500px 0px";
      var mobileRootMargin = placeholder.getAttribute("data-lazy-root-margin-mobile") || desktopRootMargin;
      var isMobile = window.matchMedia && window.matchMedia("(max-width: 767.98px)").matches;
      var rootMargin = isMobile ? mobileRootMargin : desktopRootMargin;
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            io.unobserve(entry.target);
            loadSection(entry.target);
          });
        },
        { rootMargin: rootMargin, threshold: 0 }
      );
      io.observe(placeholder);
    }

    if (requireScroll && window.scrollY < 10) {
      var onScroll = function () {
        if (window.scrollY < 10) return;
        window.removeEventListener("scroll", onScroll);
        startObserver();
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      return;
    }

    startObserver();
  }

  function scan(root) {
    (root || document).querySelectorAll("[data-lazy-section][data-section-id]").forEach(observe);
  }

  window.BoyaLazySection = {
    load: loadSection,
    loadIn: loadIn,
    scan: scan,
    shouldManualLoad: shouldManualLoad,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      scan(document);
    });
  } else {
    scan(document);
  }

  document.addEventListener("shopify:section:load", function (event) {
    if (event && event.target) scan(event.target);
  });
})();
